import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Menu, Radio, Shield, Sparkles, X } from "lucide-react";
import { adminEmails } from "../../../constants.ts";
import { db } from "../db.ts";
import { Auth } from "../components/Auth.tsx";
import { EventList } from "../components/EventList.tsx";
import { EventSwiper } from "../components/EventSwiper.tsx";
import { Spinner } from "../components/Spinner.tsx";
import { AdminPage } from "./AdminPage.tsx";

type Tab = "events" | "taste" | "admin";

const tabDef = (id: Tab, label: string, icon: typeof Radio) => ({
  id,
  label,
  icon,
});

const baseTabs = [
  tabDef("events", "Events", Radio),
  tabDef("taste", "Train Your Taste", Sparkles),
];

const adminTab = tabDef("admin", "Admin", Shield);

const TabButton = (
  { tab, active, onClick }: {
    tab: typeof baseTabs[number];
    active: boolean;
    onClick: () => void;
  },
) => {
  const Icon = tab.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
        active
          ? "bg-purple-600 text-white shadow-lg shadow-purple-500/20"
          : "text-gray-400 hover:text-white hover:bg-white/5"
      }`}
    >
      <Icon size={16} />
      {tab.label}
    </button>
  );
};

const TabContent = ({ tab }: { tab: Tab }) => {
  if (tab === "events") return <EventList />;
  if (tab === "taste") return <EventSwiper />;
  if (tab === "admin") return <AdminPage />;
  return null;
};

export const AppPage = () => {
  const { isLoading, user, error } = db.useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isAdmin = user?.email && adminEmails.includes(user.email);

  const getActiveTabFromPath = (pathname: string): Tab => {
    if (pathname.startsWith("/swipe")) return "taste";
    if (pathname.startsWith("/admin") && isAdmin) return "admin";
    return "events";
  };

  const activeTab = getActiveTabFromPath(location.pathname);

  const handleTabChange = (tabId: Tab) => {
    if (tabId === "taste") {
      navigate("/swipe");
    } else if (tabId === "admin") {
      navigate("/admin");
    } else {
      navigate("/events");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-[#1a1a1a] to-black">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-[#1a1a1a] to-black">
        <p className="text-red-400">Auth error: {error.message}</p>
      </div>
    );
  }

  if (!user) return <Auth />;

  const tabs = isAdmin ? [...baseTabs, adminTab] : baseTabs;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a1a1a] to-black text-white">
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#1a1a1a]/80 border-b border-white/5">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          {/* Logo / Branding */}
          <Link
            to="/"
            className="text-lg font-extrabold tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-500 to-red-500 transition-opacity hover:opacity-90"
          >
            Agent FOMO
          </Link>

          {/* Desktop Navigation (hidden on mobile) */}
          <div className="hidden md:flex items-center gap-6">
            <div className="flex items-center gap-1">
              {tabs.map((tab) => (
                <TabButton
                  key={tab.id}
                  tab={tab}
                  active={activeTab === tab.id}
                  onClick={() => handleTabChange(tab.id)}
                />
              ))}
            </div>

            <div className="h-4 w-[1px] bg-white/10" />

            <div className="flex items-center gap-4">
              <Link
                to="/docs"
                className="text-xs font-medium text-purple-400 hover:text-purple-300 transition"
              >
                Developer API
              </Link>
              <a
                href="/llms.txt"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-medium text-gray-400 hover:text-white transition"
              >
                llms.txt
              </a>
              <button
                type="button"
                onClick={() => db.auth.signOut()}
                className="flex items-center gap-2 text-gray-400 hover:text-white transition ml-2"
              >
                <LogOut size={18} />
              </button>
            </div>
          </div>

          {/* Mobile Menu Toggle Button (hidden on desktop) */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-gray-400 hover:text-white transition"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation Drawer (hidden on desktop) */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/5 bg-[#1a1a1a] px-4 py-4 space-y-4 animate-fade-in">
            {/* Tabs */}
            <div className="flex flex-col gap-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => {
                      handleTabChange(tab.id);
                      setMobileMenuOpen(false);
                    }}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                      active
                        ? "bg-purple-600 text-white shadow-lg shadow-purple-500/20"
                        : "text-gray-400 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    <Icon size={18} />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            <div className="border-t border-white/5 my-2" />

            {/* Additional Links & Logout */}
            <div className="flex flex-col gap-3 px-2">
              <Link
                to="/docs"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-medium text-purple-400 hover:text-purple-300 transition py-1"
              >
                Developer API
              </Link>
              <a
                href="/llms.txt"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-medium text-gray-400 hover:text-white transition py-1"
              >
                llms.txt
              </a>
              <button
                type="button"
                onClick={() => {
                  db.auth.signOut();
                  setMobileMenuOpen(false);
                }}
                className="flex items-center gap-2 text-red-400 hover:text-red-300 transition py-1 text-sm font-medium mt-1"
              >
                <LogOut size={18} />
                Sign Out
              </button>
            </div>
          </div>
        )}
      </header>
      <main>
        <TabContent tab={activeTab} />
      </main>
    </div>
  );
};
